import React, { useState, useEffect, useMemo } from 'react';
import {
    X, Calendar, Clock, Video, Phone, MapPin, Link2, User, Mail,
    Loader2, ChevronLeft, ChevronRight, Users, FileText, XCircle, Check, Globe
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createBooking, updateBooking, fetchAvailableSlots, cancelBooking } from '../api/calendar';

const DURATION_OPTIONS = [15, 30, 45, 60];

const BookingModal = ({ booking, eventTypes = [], cases = [], onSave, onCancel, onClose, isLoadingEventTypes = false }) => {
    const isViewing = booking?.id;
    const isCreating = !isViewing;
    const initialEventType = booking?.eventType || booking?.event_type || null;

    // Form State
    const [selectedEventType, setSelectedEventType] = useState(initialEventType);
    const [selectedDuration, setSelectedDuration] = useState(30);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [availableSlots, setAvailableSlots] = useState({});
    const [loadingSlots, setLoadingSlots] = useState(true);
    const [step, setStep] = useState(1); // 1 = select time, 2 = enter details
    const [timeFormat, setTimeFormat] = useState('12h');

    // Attendee form state
    const [attendees, setAttendees] = useState([{ name: '', email: '', timezone: '' }]);
    const [selectedCaseId, setSelectedCaseId] = useState(booking?.case_id || '');
    const [notes, setNotes] = useState(booking?.notes || '');
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState({});

    // Calendar State
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [weekStartDate, setWeekStartDate] = useState(() => {
        const today = new Date();
        const day = today.getDay();
        const diff = today.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(today.getFullYear(), today.getMonth(), diff);
    });

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Generate week days (5 weekdays)
    const weekDays = useMemo(() => {
        const days = [];
        const start = new Date(weekStartDate);
        for (let i = 0; i < 5; i++) {
            const date = new Date(start);
            date.setDate(start.getDate() + i);
            days.push(date);
        }
        return days;
    }, [weekStartDate]);

    // Load slots from API when event type or week changes
    useEffect(() => {
        if (isCreating && selectedEventType?.id) {
            loadSlots();
        } else if (isCreating && !selectedEventType?.id) {
            // Clear slots if no event type selected
            setAvailableSlots({});
            setLoadingSlots(false);
        }
    }, [selectedEventType, weekStartDate, isCreating]);

    const loadSlots = async () => {
        if (!selectedEventType?.id) {
            setAvailableSlots({});
            return;
        }

        setLoadingSlots(true);
        try {
            const startDate = weekDays[0];
            const endDate = weekDays[weekDays.length - 1];

            const res = await fetchAvailableSlots({
                eventTypeId: selectedEventType.id,
                startDate: startDate.toISOString().split('T')[0],
                endDate: endDate.toISOString().split('T')[0],
                timezone
            });

            // API returns slots grouped by date - use directly
            setAvailableSlots(res.data?.slots || {});
        } catch (err) {
            console.error('Failed to load slots:', err);
            setAvailableSlots({});
            setErrors({ slots: 'Failed to load available times. Please try again.' });
        } finally {
            setLoadingSlots(false);
        }
    };

    // Calendar helpers
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDay = (firstDay.getDay() + 6) % 7;

        const days = [];
        for (let i = 0; i < startingDay; i++) {
            days.push(null);
        }
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }
        return days;
    };

    const isDateAvailable = (date) => {
        if (!date) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date < today) return false;
        if (date.getDay() === 0 || date.getDay() === 6) return false;
        return true;
    };

    const handleDateSelect = (date) => {
        if (!isDateAvailable(date)) return;
        setSelectedDate(date);
        const day = date.getDay();
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        setWeekStartDate(new Date(date.getFullYear(), date.getMonth(), diff));
    };

    const navigateWeek = (direction) => {
        const newDate = new Date(weekStartDate);
        newDate.setDate(weekStartDate.getDate() + (direction * 7));
        setWeekStartDate(newDate);
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        if (timeFormat === '24h') {
            return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
        }
        return date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        }).toLowerCase();
    };

    const formatWeekRange = () => {
        const start = weekDays[0];
        const end = weekDays[weekDays.length - 1];
        return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { day: 'numeric' })}, ${end.getFullYear()}`;
    };

    const handleSlotSelect = (slot) => {
        // Require event type before proceeding
        if (!selectedEventType?.id) {
            setErrors({ eventType: 'Please select an event type first' });
            return;
        }
        setSelectedSlot(slot);
        setStep(2);
    };

    const handleBack = () => {
        setStep(1);
        setSelectedSlot(null);
    };

    const validate = () => {
        const newErrors = {};
        if (!selectedEventType?.id) newErrors.eventType = 'Please select an event type';
        if (!selectedSlot) newErrors.slot = 'Please select a time slot';
        if (!attendees[0].name.trim()) newErrors.attendeeName = 'Name is required';
        if (!attendees[0].email.trim()) {
            newErrors.attendeeEmail = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(attendees[0].email)) {
            newErrors.attendeeEmail = 'Invalid email address';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setIsSaving(true);
        try {
            const payload = {
                event_type_id: selectedEventType.id,
                start_time: selectedSlot.start,
                end_time: selectedSlot.end,
                attendees: attendees.filter(a => a.name && a.email).map(a => ({
                    name: a.name.trim(),
                    email: a.email.trim(),
                    timezone: a.timezone || timezone
                })),
                case_id: selectedCaseId || null,
                notes: notes.trim() || null
            };

            const result = await createBooking(payload);
            onSave?.(result.data);
        } catch (err) {
            console.error('Failed to create booking:', err);
            setErrors({ submit: err.message });
        } finally {
            setIsSaving(false);
        }
    };

    const getLocationIcon = (locationType) => {
        switch (locationType) {
            case 'video': return Video;
            case 'phone': return Phone;
            case 'in_person': return MapPin;
            default: return Link2;
        }
    };

    // Viewing existing booking
    if (isViewing) {
        const LocationIcon = getLocationIcon(booking.location_type);
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
                <div className="absolute inset-0 dark:bg-slate-950/80 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
                <motion.div
                    initial={{ scale: 0.95, y: 20 }}
                    animate={{ scale: 1, y: 0 }}
                    className="relative w-full max-w-lg dark:bg-slate-900 bg-white rounded-2xl shadow-2xl overflow-hidden"
                >
                    <div className="h-2" style={{ backgroundColor: booking.event_type?.color || '#3B82F6' }} />
                    <div className="px-6 py-4 border-b dark:border-slate-800 border-slate-200">
                        <div className="flex items-center justify-between">
                            <h2 className="text-xl font-bold dark:text-white text-slate-900">Booking Details</h2>
                            <button onClick={onClose} className="p-2 rounded-lg dark:text-slate-400 text-slate-500 dark:hover:bg-slate-800 hover:bg-slate-100 transition">
                                <X size={20} />
                            </button>
                        </div>
                    </div>
                    <div className="p-6 space-y-4">
                        <h3 className="text-lg font-semibold dark:text-white text-slate-900">{booking.title}</h3>
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg dark:bg-slate-800 bg-slate-100">
                                    <Calendar size={18} className="text-sky-500" />
                                </div>
                                <div>
                                    <p className="font-medium dark:text-white text-slate-900">
                                        {new Date(booking.start_time).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                                    </p>
                                    <p className="text-sm dark:text-slate-400 text-slate-500">
                                        {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg dark:bg-slate-800 bg-slate-100">
                                    <Clock size={18} className="text-sky-500" />
                                </div>
                                <p className="dark:text-slate-300 text-slate-700">{booking.event_type?.duration || 30} minutes</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg dark:bg-slate-800 bg-slate-100">
                                    <LocationIcon size={18} className="text-sky-500" />
                                </div>
                                <p className="dark:text-slate-300 text-slate-700 capitalize">{booking.location_type?.replace('_', ' ') || 'Video call'}</p>
                            </div>
                            {booking.attendees?.length > 0 && (
                                <div className="flex items-start gap-3">
                                    <div className="p-2 rounded-lg dark:bg-slate-800 bg-slate-100">
                                        <Users size={18} className="text-sky-500" />
                                    </div>
                                    <div>
                                        {booking.attendees.map((att, i) => (
                                            <div key={i} className="mb-1">
                                                <p className="dark:text-slate-300 text-slate-700">{att.name}</p>
                                                <p className="text-sm dark:text-slate-500 text-slate-400">{att.email}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <div className="pt-3 border-t dark:border-slate-800 border-slate-200">
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium capitalize ${
                                    booking.status === 'confirmed' ? 'bg-emerald-500/10 text-emerald-500' :
                                    booking.status === 'pending' ? 'bg-amber-500/10 text-amber-500' :
                                    'bg-red-500/10 text-red-500'
                                }`}>
                                    {booking.status}
                                </span>
                            </div>
                        </div>
                    </div>
                    {booking.status !== 'cancelled' && (
                        <div className="px-6 py-4 border-t dark:border-slate-800 border-slate-200 flex justify-end">
                            <button
                                onClick={() => onCancel?.(booking.id, 'Cancelled by host')}
                                className="px-4 py-2 text-red-500 hover:bg-red-500/10 rounded-lg font-medium transition flex items-center gap-2"
                            >
                                <XCircle size={16} />
                                Cancel Booking
                            </button>
                        </div>
                    )}
                </motion.div>
            </motion.div>
        );
    }

    // Creating new booking - Step 1: Select Time (cal.com style)
    if (step === 1) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4"
            >
                <div className="absolute inset-0 dark:bg-slate-950/80 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
                <motion.div
                    initial={{ scale: 0.95, y: 20 }}
                    animate={{ scale: 1, y: 0 }}
                    className="relative w-full max-w-4xl max-h-[85vh] dark:bg-slate-900 bg-white rounded-2xl shadow-2xl overflow-hidden flex"
                >
                    {/* Left Panel - Event Info & Calendar */}
                    <div className="w-64 border-r dark:border-slate-800 border-slate-200 p-5 flex flex-col overflow-y-auto flex-shrink-0">
                        {/* Event Type Selection */}
                        {eventTypes.length > 0 && !initialEventType && (
                            <div className="mb-4">
                                <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-2">
                                    Event Type
                                </label>
                                <div className="space-y-1.5 max-h-32 overflow-y-auto">
                                    {eventTypes.filter(et => et.is_active !== false).map(et => (
                                        <button
                                            key={et.id}
                                            type="button"
                                            onClick={() => {
                                                setSelectedEventType(et);
                                                setSelectedDuration(et.duration || 30);
                                                setSelectedSlot(null);
                                                setErrors({}); // Clear errors when selecting event type
                                            }}
                                            className={`w-full p-2 rounded-lg border text-left transition text-sm ${
                                                selectedEventType?.id === et.id
                                                    ? 'border-sky-500 dark:bg-sky-500/10 bg-sky-50'
                                                    : 'dark:border-slate-700 border-slate-200 hover:border-slate-300 dark:hover:border-slate-600'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-2 h-2 rounded-full flex-shrink-0"
                                                    style={{ backgroundColor: et.color || '#3B82F6' }}
                                                />
                                                <span className="font-medium dark:text-white text-slate-900 truncate">{et.title}</span>
                                                <span className="text-xs dark:text-slate-400 text-slate-500 ml-auto">{et.duration}m</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                {errors.eventType && (
                                    <p className="text-xs text-red-500 mt-1">{errors.eventType}</p>
                                )}
                            </div>
                        )}

                        {/* Event Title (when selected) */}
                        {selectedEventType && (
                            <div className="mb-4">
                                <h2 className="text-lg font-semibold dark:text-white text-slate-900">
                                    {selectedEventType.title}
                                </h2>
                                {selectedEventType.description && (
                                    <p className="text-sm dark:text-slate-400 text-slate-500 mt-1">
                                        {selectedEventType.description}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* No Event Types Message */}
                        {eventTypes.length === 0 && !isLoadingEventTypes && (
                            <div className="mb-4 p-3 rounded-lg border-2 border-dashed dark:border-slate-700 border-slate-200 text-center">
                                <Calendar size={24} className="mx-auto mb-2 dark:text-slate-600 text-slate-400" />
                                <p className="text-sm font-medium dark:text-slate-300 text-slate-600">No event types</p>
                                <p className="text-xs dark:text-slate-500 text-slate-400 mt-1">
                                    Create an event type first
                                </p>
                            </div>
                        )}

                        {/* Duration Display (from event type) */}
                        {selectedEventType && (
                            <div className="flex items-center gap-2 mb-2 text-sm dark:text-slate-400 text-slate-500">
                                <Clock size={14} />
                                <span>{selectedEventType.duration} minutes</span>
                            </div>
                        )}

                        {/* Location */}
                        <div className="flex items-center gap-2 mb-2 text-sm dark:text-slate-400 text-slate-500">
                            <Video size={14} />
                            <span>Cal Video</span>
                        </div>

                        {/* Timezone */}
                        <div className="flex items-center gap-2 mb-4 text-sm dark:text-slate-400 text-slate-500">
                            <Globe size={14} />
                            <span className="truncate text-xs">{timezone}</span>
                        </div>

                        {/* Mini Calendar */}
                        <div className="mt-auto pt-4 border-t dark:border-slate-800 border-slate-200">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-sm font-medium dark:text-white text-slate-900">
                                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                </h3>
                                <div className="flex">
                                    <button
                                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
                                        className="p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                                    >
                                        <ChevronLeft size={14} className="dark:text-slate-400 text-slate-500" />
                                    </button>
                                    <button
                                        onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
                                        className="p-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                                    >
                                        <ChevronRight size={14} className="dark:text-slate-400 text-slate-500" />
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-7 gap-0 text-center">
                                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => (
                                    <div key={i} className="p-1 text-[10px] font-medium dark:text-slate-500 text-slate-400">
                                        {day}
                                    </div>
                                ))}
                                {getDaysInMonth(currentMonth).map((day, i) => {
                                    if (!day) return <div key={`empty-${i}`} className="p-1" />;
                                    const isAvailable = isDateAvailable(day);
                                    const isSelected = selectedDate?.toDateString() === day.toDateString();
                                    const isToday = new Date().toDateString() === day.toDateString();
                                    const isInWeek = weekDays.some(wd => wd.toDateString() === day.toDateString());

                                    return (
                                        <button
                                            key={day.toISOString()}
                                            onClick={() => handleDateSelect(day)}
                                            disabled={!isAvailable}
                                            className={`p-1 text-xs rounded transition ${
                                                isSelected
                                                    ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-medium'
                                                    : isInWeek && isAvailable
                                                    ? 'dark:bg-slate-800 bg-slate-200 dark:text-white text-slate-900'
                                                    : isToday
                                                    ? 'dark:bg-slate-700 bg-slate-300 dark:text-white text-slate-900 font-medium'
                                                    : isAvailable
                                                    ? 'dark:text-white text-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800'
                                                    : 'dark:text-slate-700 text-slate-300 cursor-not-allowed'
                                            }`}
                                        >
                                            {day.getDate()}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Right Panel - Time Slots */}
                    <div className="flex-1 flex flex-col min-w-0">
                        {/* Header with Week Navigation */}
                        <div className="px-4 py-3 border-b dark:border-slate-800 border-slate-200 flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <span className="font-medium dark:text-white text-slate-900">{formatWeekRange()}</span>
                                <div className="flex">
                                    <button
                                        onClick={() => navigateWeek(-1)}
                                        className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                                    >
                                        <ChevronLeft size={16} className="dark:text-slate-400 text-slate-500" />
                                    </button>
                                    <button
                                        onClick={() => navigateWeek(1)}
                                        className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                                    >
                                        <ChevronRight size={16} className="dark:text-slate-400 text-slate-500" />
                                    </button>
                                </div>
                            </div>

                            {/* Time Format Toggle */}
                            <div className="flex dark:bg-slate-800 bg-slate-100 rounded p-0.5">
                                <button
                                    onClick={() => setTimeFormat('12h')}
                                    className={`px-2 py-0.5 text-xs rounded transition ${
                                        timeFormat === '12h'
                                            ? 'dark:bg-slate-600 bg-white dark:text-white text-slate-900 shadow-sm'
                                            : 'dark:text-slate-400 text-slate-500'
                                    }`}
                                >
                                    12h
                                </button>
                                <button
                                    onClick={() => setTimeFormat('24h')}
                                    className={`px-2 py-0.5 text-xs rounded transition ${
                                        timeFormat === '24h'
                                            ? 'dark:bg-slate-600 bg-white dark:text-white text-slate-900 shadow-sm'
                                            : 'dark:text-slate-400 text-slate-500'
                                    }`}
                                >
                                    24h
                                </button>
                            </div>

                            <button
                                onClick={onClose}
                                className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800"
                            >
                                <X size={18} className="dark:text-slate-400 text-slate-500" />
                            </button>
                        </div>

                        {/* Day Headers */}
                        <div className="grid grid-cols-5 border-b dark:border-slate-800 border-slate-200 flex-shrink-0">
                            {weekDays.map((day) => {
                                const isToday = new Date().toDateString() === day.toDateString();
                                return (
                                    <div
                                        key={day.toISOString()}
                                        className={`py-2 text-center border-r last:border-r-0 dark:border-slate-800 border-slate-200 ${
                                            isToday ? 'dark:bg-slate-800/50 bg-sky-50' : ''
                                        }`}
                                    >
                                        <div className={`text-xs font-medium uppercase ${
                                            isToday ? 'text-sky-500' : 'dark:text-slate-400 text-slate-500'
                                        }`}>
                                            {day.toLocaleDateString('en-US', { weekday: 'short' })}
                                        </div>
                                        <div className={`text-base font-bold ${
                                            isToday
                                                ? 'w-7 h-7 rounded-full bg-sky-500 text-white flex items-center justify-center mx-auto'
                                                : 'dark:text-white text-slate-900'
                                        }`}>
                                            {day.getDate()}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Time Slots Grid */}
                        <div className="flex-1 overflow-y-auto">
                            {!selectedEventType?.id ? (
                                <div className="flex items-center justify-center py-16">
                                    <div className="text-center">
                                        <Calendar size={32} className="mx-auto mb-3 dark:text-slate-600 text-slate-300" />
                                        <p className="text-sm dark:text-slate-400 text-slate-500">
                                            Select an event type to see available times
                                        </p>
                                    </div>
                                </div>
                            ) : loadingSlots ? (
                                <div className="flex items-center justify-center py-16">
                                    <Loader2 size={24} className="animate-spin text-sky-500" />
                                </div>
                            ) : errors.slots ? (
                                <div className="flex items-center justify-center py-16">
                                    <div className="text-center">
                                        <p className="text-sm text-red-500">{errors.slots}</p>
                                        <button
                                            onClick={loadSlots}
                                            className="mt-2 text-sm text-sky-500 hover:underline"
                                        >
                                            Try again
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-5">
                                    {weekDays.map((day) => {
                                        const dateStr = day.toISOString().split('T')[0];
                                        const slots = availableSlots[dateStr] || [];
                                        const today = new Date();
                                        today.setHours(0, 0, 0, 0);
                                        const isPast = day < today;
                                        const isWeekend = day.getDay() === 0 || day.getDay() === 6;

                                        return (
                                            <div
                                                key={dateStr}
                                                className="border-r last:border-r-0 dark:border-slate-800 border-slate-200 p-1"
                                            >
                                                {isPast || isWeekend || slots.length === 0 ? (
                                                    <div className="flex items-center justify-center py-12">
                                                        <p className="text-[10px] dark:text-slate-600 text-slate-400">
                                                            {isPast ? 'Past' : isWeekend ? 'Off' : 'No slots'}
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-1">
                                                        {slots.map((slot, i) => (
                                                            <button
                                                                key={i}
                                                                onClick={() => handleSlotSelect(slot)}
                                                                className="w-full py-1.5 text-sm font-medium rounded border dark:border-slate-700 border-slate-200 dark:text-white text-slate-900 hover:border-sky-500 dark:hover:border-sky-500 hover:bg-sky-50 dark:hover:bg-sky-500/10 transition-colors"
                                                            >
                                                                {formatTime(slot.start)}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        );
    }

    // Step 2: Enter Details (cal.com confirmation style)
    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
            <div className="absolute inset-0 dark:bg-slate-950/80 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
            <motion.div
                initial={{ scale: 0.95, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                className="relative w-full max-w-2xl dark:bg-slate-900 bg-white rounded-2xl shadow-2xl overflow-hidden flex"
            >
                {/* Left Panel - Booking Summary */}
                <div className="w-64 border-r dark:border-slate-800 border-slate-200 p-5 dark:bg-slate-900/50 bg-slate-50">
                    <h2 className="text-lg font-semibold dark:text-white text-slate-900 mb-1">
                        {selectedEventType?.title || `${selectedDuration} Minutes`}
                    </h2>
                    {selectedEventType?.description && (
                        <p className="text-sm dark:text-slate-400 text-slate-500 mb-4 line-clamp-2">
                            {selectedEventType.description}
                        </p>
                    )}

                    <div className="space-y-3 text-sm">
                        <div className="flex items-start gap-2 dark:text-slate-300 text-slate-600">
                            <Calendar size={16} className="text-slate-400 mt-0.5 flex-shrink-0" />
                            <span>{new Date(selectedSlot.start).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        <div className="flex items-center gap-2 dark:text-slate-300 text-slate-600">
                            <Clock size={16} className="text-slate-400 flex-shrink-0" />
                            <span>{formatTime(selectedSlot.start)} - {formatTime(selectedSlot.end)}</span>
                        </div>
                        <div className="flex items-center gap-2 dark:text-slate-300 text-slate-600">
                            <Video size={16} className="text-slate-400 flex-shrink-0" />
                            <span>Cal Video</span>
                        </div>
                        <div className="flex items-center gap-2 dark:text-slate-300 text-slate-600">
                            <Globe size={16} className="text-slate-400 flex-shrink-0" />
                            <span className="truncate">{timezone}</span>
                        </div>
                    </div>

                    {/* Event Type Badge */}
                    {selectedEventType && (
                        <div className="mt-4 pt-4 border-t dark:border-slate-800 border-slate-200">
                            <div className="flex items-center gap-2">
                                <div
                                    className="w-2 h-2 rounded-full"
                                    style={{ backgroundColor: selectedEventType.color || '#3B82F6' }}
                                />
                                <span className="text-xs dark:text-slate-400 text-slate-500">
                                    {selectedEventType.duration} min · {selectedEventType.location_type === 'video' ? 'Video Call' : selectedEventType.location_type || 'Video Call'}
                                </span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Panel - Form */}
                <div className="flex-1 p-5">
                    <form onSubmit={handleSubmit}>
                        <div className="space-y-4">
                            {/* Name */}
                            <div>
                                <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-1">
                                    Your name *
                                </label>
                                <input
                                    type="text"
                                    value={attendees[0].name}
                                    onChange={(e) => setAttendees([{ ...attendees[0], name: e.target.value }])}
                                    className={`w-full px-3 py-2 rounded-lg border dark:bg-slate-800 bg-white dark:text-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500 ${
                                        errors.attendeeName ? 'border-red-500' : 'dark:border-slate-700 border-slate-300'
                                    }`}
                                />
                                {errors.attendeeName && <p className="text-xs text-red-500 mt-1">{errors.attendeeName}</p>}
                            </div>

                            {/* Email */}
                            <div>
                                <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-1">
                                    Email address *
                                </label>
                                <input
                                    type="email"
                                    value={attendees[0].email}
                                    onChange={(e) => setAttendees([{ ...attendees[0], email: e.target.value }])}
                                    className={`w-full px-3 py-2 rounded-lg border dark:bg-slate-800 bg-white dark:text-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500 ${
                                        errors.attendeeEmail ? 'border-red-500' : 'dark:border-slate-700 border-slate-300'
                                    }`}
                                />
                                {errors.attendeeEmail && <p className="text-xs text-red-500 mt-1">{errors.attendeeEmail}</p>}
                            </div>

                            {/* Link to Case */}
                            {cases.length > 0 && (
                                <div>
                                    <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-1">
                                        Link to Case (Optional)
                                    </label>
                                    <select
                                        value={selectedCaseId}
                                        onChange={(e) => setSelectedCaseId(e.target.value)}
                                        className="w-full px-3 py-2 rounded-lg border dark:border-slate-700 border-slate-300 dark:bg-slate-800 bg-white dark:text-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500"
                                    >
                                        <option value="">No case linked</option>
                                        {cases.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.caseNumber || c.case_number} - {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Notes */}
                            <div>
                                <label className="block text-sm font-medium dark:text-slate-300 text-slate-700 mb-1">
                                    Additional notes
                                </label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={3}
                                    className="w-full px-3 py-2 rounded-lg border dark:border-slate-700 border-slate-300 dark:bg-slate-800 bg-white dark:text-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500/50 focus:border-sky-500 resize-none"
                                    placeholder="Please share anything that will help prepare for our meeting..."
                                />
                            </div>
                        </div>

                        {(errors.submit || errors.eventType) && (
                            <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                                <p className="text-sm text-red-500">{errors.submit || errors.eventType}</p>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center justify-between mt-6 pt-4 border-t dark:border-slate-800 border-slate-200">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="px-4 py-2 text-sm dark:text-slate-300 text-slate-600 hover:underline"
                            >
                                Back
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="px-5 py-2 bg-slate-900 dark:bg-white hover:bg-slate-800 dark:hover:bg-slate-100 text-white dark:text-slate-900 rounded-lg font-medium text-sm flex items-center gap-2 transition disabled:opacity-50"
                            >
                                {isSaving && <Loader2 size={14} className="animate-spin" />}
                                Confirm
                            </button>
                        </div>
                    </form>
                </div>
            </motion.div>
        </motion.div>
    );
};

export default BookingModal;
